/**
 * TakeoverBackHeader — the `‹ Somewhere` row that sits on top of a surface
 * which has REPLACED what the person was looking at.
 *
 * Extracted from `MoreInformationSection`, which invented it: opening that
 * pane replaces the run body rather than expanding an accordion downward, so
 * the only way back is a row that says where back is. The pane's run drawer
 * needs the same row, so the chrome lives here once instead of being retyped
 * per takeover.
 *
 * It is CONTROLLED and dumb: the host owns whether its surface is showing and
 * what "back" means. Shared, so no Office.js and no Remix import — the Outlook
 * pane and ora both mount it.
 *
 * The glyph is `←`, matching the run drawer's back row — the two are the
 * same gesture and looked like two different ones while this used `‹`.
 *
 * The whole row is the button, not an icon in a corner: at task-pane width a
 * 12px chevron is a poor target, and the label is the part that carries the
 * information. `touch-target` gives it a 44px floor on a coarse pointer only
 * (see app/tailwind.css) so the desktop and the pane stay compact.
 */
import CustomTooltip from "~/common/components/CustomTooltip";

export interface TakeoverBackHeaderProps {
  /** Where back goes, in the host's words — "More information" names the pane
   *  being left, "Back to your queue" names the destination. Both read
   *  correctly after the `‹`; the host picks whichever is true for it. */
  label: string;
  onBack: () => void;
  /** Overridden by callers with an established id (MoreInformationSection's
   *  `more-information-back` is referenced by its own suite). */
  testId?: string;
}

export function TakeoverBackHeader({
  label,
  onBack,
  testId = "takeover-back",
}: TakeoverBackHeaderProps) {
  return (
    <button
      type="button"
      onClick={onBack}
      className="touch-target flex w-full flex-shrink-0 items-center gap-2 border-b border-primary-gray-light-200 bg-base-white px-3 py-2.5 text-left text-sm font-medium text-primary-gray-light-900 hover:bg-primary-gray-light-50"
      data-testid={testId}
    >
      <span aria-hidden="true">←</span>
      {/* The full label when it truncates, in ora's tooltip (story 28633). */}
      {label ? (
        <CustomTooltip
          id={`${testId}-label`}
          title={label}
          placement="bottom-start"
          arrow
          isOnModal
        >
          <span className="min-w-0 truncate">{label}</span>
        </CustomTooltip>
      ) : null}
    </button>
  );
}

export default TakeoverBackHeader;
